/**
 * StatusPill — 1px outlined pill (no fill). Color + Swedish label always together.
 * Status machine (Architecture):
 *   DISCOVERED → QUEUED → SUBMITTED → ACKNOWLEDGED
 *     → REMOVED | DENIED | NEEDS_USER_ACTION | REAPPEARED
 *
 * Shared by Home (Agent 6), pipeline lists (Agent 7), and stubs.
 */
import type { CSSProperties } from 'react';

export type RemovalStatus =
  | 'DISCOVERED'
  | 'QUEUED'
  | 'SUBMITTED'
  | 'ACKNOWLEDGED'
  | 'REMOVED'
  | 'DENIED'
  | 'NEEDS_USER_ACTION'
  | 'REAPPEARED';

/** Alias used by Home mock */
export type StatusKey = RemovalStatus;

/** Hex fallbacks if CSS status tokens missing */
const FALLBACK: Record<RemovalStatus, string> = {
  DISCOVERED: '#5B6B7C',
  QUEUED: '#1a3a7a',
  SUBMITTED: '#1a3a7a',
  ACKNOWLEDGED: '#1a3a7a',
  REMOVED: '#0F7B4B',
  DENIED: '#B42318',
  NEEDS_USER_ACTION: '#C47F00',
  REAPPEARED: '#B42318',
};

export const STATUS_META: Record<
  RemovalStatus,
  { labelSv: string; color: string; colorFallback: string }
> = {
  DISCOVERED: {
    labelSv: 'Upptäckt',
    color: 'var(--status-discovered)',
    colorFallback: FALLBACK.DISCOVERED,
  },
  QUEUED: {
    labelSv: 'I kö',
    color: 'var(--status-queued)',
    colorFallback: FALLBACK.QUEUED,
  },
  SUBMITTED: {
    labelSv: 'Inskickad',
    color: 'var(--status-submitted)',
    colorFallback: FALLBACK.SUBMITTED,
  },
  ACKNOWLEDGED: {
    labelSv: 'Mottagen',
    color: 'var(--status-acknowledged)',
    colorFallback: FALLBACK.ACKNOWLEDGED,
  },
  REMOVED: {
    labelSv: 'Borttagen',
    color: 'var(--status-removed)',
    colorFallback: FALLBACK.REMOVED,
  },
  DENIED: {
    labelSv: 'Nekad',
    color: 'var(--status-denied)',
    colorFallback: FALLBACK.DENIED,
  },
  NEEDS_USER_ACTION: {
    labelSv: 'Kräver din åtgärd',
    color: 'var(--status-needs-user-action)',
    colorFallback: FALLBACK.NEEDS_USER_ACTION,
  },
  REAPPEARED: {
    labelSv: 'Återkommen',
    color: 'var(--status-reappeared)',
    colorFallback: FALLBACK.REAPPEARED,
  },
};

export const ALL_STATUSES: RemovalStatus[] = [
  'DISCOVERED',
  'QUEUED',
  'SUBMITTED',
  'ACKNOWLEDGED',
  'REMOVED',
  'DENIED',
  'NEEDS_USER_ACTION',
  'REAPPEARED',
];

export function getStatusMeta(status: RemovalStatus) {
  const m = STATUS_META[status];
  return { label: m.labelSv, color: m.colorFallback };
}

type StatusPillProps = {
  status: RemovalStatus;
  /** Override Swedish label (rare) */
  label?: string;
  className?: string;
  style?: CSSProperties;
};

export function StatusPill({ status, label, className, style }: StatusPillProps) {
  const meta = STATUS_META[status];
  const text = label ?? meta.labelSv;
  // Prefer design-token color; hex fallback via CSS custom property cascade
  const color = meta.color;

  return (
    <span
      className={className}
      data-status={status}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        letterSpacing: '0.05em',
        fontWeight: 400,
        lineHeight: 1.3,
        padding: '3px 10px',
        borderRadius: 99,
        border: `1px solid ${color}`,
        color,
        background: 'transparent',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--font-body, Lora, Georgia, serif)',
        fontFeatureSettings: '"tnum" 1',
        ...style,
      }}
    >
      {text}
    </span>
  );
}

export default StatusPill;
